import React, { useContext, useState } from "react"
import { isMobile } from "react-device-detect"
import { UserContext } from "../../HomeApp"
import { ShareUrlButton } from "../../ShareUrlButton"
import PreviewPlayer from "../../PreviewPlayer"
import {
  FollowComponent,
  FollowComponentLoggedOut,
} from "../../shared/FollowComponent"
import { BandSocialIcons } from "../../bands/BandSocialIcons"
import { Backline } from "../../Backline"
import type { Band } from "../../../types/bands"

interface BandHeaderProps {
  band: Band
  headerMode?: "classic" | string
}

const defaultBackgroundGifDep =
  "https://bandnada.s3.amazonaws.com/static/gifs/bandnada_with_slogans_edit_1.gif"
const defaultBackgroundGif =
  "https://bandnada.s3.amazonaws.com/static/external_assets/web/bandnada_animated_logo_static_w_tag.jpg"
const defaultBackgroundStatic =
  "https://bandnada.s3.amazonaws.com/static/external_assets/web/background_logo_bottom_left_gray.png"

const BandHeader: React.FC<BandHeaderProps> = ({ band, headerMode }) => {
  const { user } = useContext(UserContext)

  const bandUrl = `${window.location.protocol}//${window.location.host}${band.band_path}`
  const isFan = band.band_type === "fan"

  const urlParams = new URLSearchParams(window.location.search)
  const [showBackline, setShowBackline] = useState(
    urlParams.get("backline") === "true",
  )
  const fadeGradient = "linear-gradient(transparent, 47%, white)"
  const blackGradient = "linear-gradient(black, 30%, transparent)"
  const angleGradient =
    "linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7))"
  const backgroundImage = band.cover_photo_url_large
    ? `url(${band.cover_photo_url_large})`
    : isMobile
      ? `url(${defaultBackgroundStatic})`
      : `url(${defaultBackgroundGif})`

  const classicBgImage = band.cover_photo_url_large
    ? `${
        isMobile ? "url('')" : fadeGradient
      }, ${blackGradient}, ${backgroundImage}`
    : isMobile
      ? `${backgroundImage}`
      : `linear-gradient(transparent, 79%, white), ${backgroundImage}`
  const bgImage = band.cover_photo_url_large
    ? `${
        isMobile ? "url('')" : fadeGradient
      }, ${angleGradient}, ${backgroundImage}`
    : `${fadeGradient}, ${angleGradient}, ${backgroundImage}`

  const backgroundStyle: React.CSSProperties = {
    aspectRatio: isMobile ? undefined : "16 / 9",
    backgroundImage: bgImage,
    backgroundSize: band.cover_photo_url_large ? "cover" : "contain",
    backgroundRepeat: "no-repeat",
    minHeight: "255px",
  }

  const classicBackgroundStyle: React.CSSProperties = {
    aspectRatio: isFan || isMobile ? undefined : "16 / 9",
    backgroundImage: isFan ? undefined : classicBgImage,
    backgroundColor: isFan ? "black" : undefined,
    backgroundSize: band.cover_photo_url_large ? "cover" : "contain",
    backgroundRepeat: "no-repeat",
    minHeight: "255px",
    flexDirection: "column",
    display: isMobile ? "flex" : "block",
  }

  const width = window.innerWidth
  const imageSliceStyle: React.CSSProperties = {
    width: 0,
    height: 0,
    borderTop: `${width * 1.91}px solid transparent`,
    borderBottom: `${width * 1.09}px solid transparent`,
    borderLeft: `${width * 2.29}px solid white`,
    transform: `rotate(70deg)`,
    marginTop: `${width * -0.33}px`,
    marginLeft: `${-width}px`,
    opacity: 0.8,
  }

  const coverImageHeader = (
    <>
      <div
        style={
          headerMode === "classic" ? classicBackgroundStyle : backgroundStyle
        }
      >
        <div className="flex lg:flex-1 flex-col lg:flex-row justify-between h-auto lg:border-b mb-4 border-white text-white px-3 lg:px-4">
          <div className="flex">
            <div className="lg:text-right relative my-2">
              <PreviewPlayer
                bandId={band.id}
                width="4rem"
                height="4rem"
                className="text-white mr-2"
              />
            </div>
            <div className="lg:col-span-9 col-span-10 flex">
              <div className="flex-grow py-2">
                <div className="pb-1">
                  <div className="flex flex-row align-items-start">
                    <h1 className="font-extrabold text-3xl uppercase leading-none">
                      {band.name}
                      <span className="block font-normal lg:inline lg:ml-2 text-sm">
                        {(() => {
                          const isSecret =
                            band.band_type === "venue" &&
                            band.additional_info?.is_location_secret &&
                            band.additional_info.is_location_secret !== "no"
                          const typeLabel = isSecret
                            ? "secret venue"
                            : band.band_type_nice
                          if (!band.location?.displayName) return typeLabel
                          if (isSecret) {
                            const city =
                              band.location.city ||
                              band.location.neighborhood ||
                              band.location.sublocality
                            return city ? `${typeLabel} in ${city}` : typeLabel
                          }
                          return `${typeLabel} ${band.band_type === "venue" ? "in" : "from"} ${band.location.displayName}`
                        })()}
                      </span>
                    </h1>
                  </div>
                  <div className="text-sm italic leading-tight py-1">
                    {band.bio}
                  </div>
                </div>

                <div className="lg:flex gap-3">
                  {!isMobile && (
                    <ShareUrlButton
                      nadaUrl={`${bandUrl}?follow=true&utm_source=band_header&utm_medium=share`}
                      renderMode="button"
                      className="mb-0"
                    />
                  )}
                  {band.band_type === "venue" &&
                    band.backline_offerings &&
                    band.backline_offerings.trim().length > 0 && (
                      <div className="self-center">
                        <button
                          onClick={() => setShowBackline(!showBackline)}
                          className="text-blue-300 font-bold focus:outline-none"
                          style={{ textShadow: "0px 0px 20px #ffffff" }}
                        >
                          VENUE DETAILS & BACKLINE
                        </button>
                      </div>
                    )}
                  <BandSocialIcons
                    band={band}
                    color="text-white"
                    margins="mr-3"
                    includeShareIcon={isMobile}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="lg:text-right relative py-2 lg:pl-2 lg:self-center flex gap-3 lg:w-auto w-48 justify-between">
            {isFan ? null : user ? (
              <FollowComponent
                bandId={band.id}
                bandName={band.name}
                className="rounded-t shrink-0 whitespace-nowrap"
              />
            ) : (
              <FollowComponentLoggedOut
                bandId={band.id}
                bandName={band.name}
                className="rounded-t shrink-0 whitespace-nowrap"
              />
            )}
          </div>
        </div>
      </div>
      {headerMode !== "classic" && (
        <div className="hidden lg:block" style={imageSliceStyle}></div>
      )}
    </>
  )

  return (
    <>
      {coverImageHeader}
      {band.band_type === "venue" && (
        <Backline
          name={band.name}
          backlineOfferings={band.backline_offerings_html || ""}
          backlineOfferingsUpdatedAt={band.backline_offerings_updated_at}
          venueInfo={band.additional_info}
          location={band.location as any}
          isOpen={showBackline}
          onClose={() => setShowBackline(false)}
        />
      )}
    </>
  )
}

export default BandHeader
